'use client'

import { Builder } from '@builder.io/react'

import { spacingBuilderInputs } from '@src/utils/builder.util'

import { Grid } from './Grid'
import { defaultBlocks } from './Grid.defaults'

Builder.registerComponent(Grid, {
  name: 'Grid',
  static: true,
  noWrap: true,
  inputs: [
    {
      name: 'containerWrapper',
      type: 'boolean',
      friendlyName: 'Include Container?',
      defaultValue: true,
    },
    {
      ...spacingBuilderInputs.innerSpacing,
      showIf: (properties) => Boolean(properties.get('containerWrapper')),
    },
    {
      ...spacingBuilderInputs.outerSpacing,
      showIf: (properties) => Boolean(properties.get('containerWrapper')),
    },
    {
      name: 'includedChildren',
      type: 'array',
      hideFromUI: true,
      subFields: [
        {
          name: 'item',
          type: 'uiBlocks',
          defaultValue: [],
        },
      ],
      defaultValue: [
        {
          item: [],
        },
      ],
    },
    {
      name: 'responsiveColumns',
      friendlyName: 'Columns Responsive Layout',
      type: 'object',
      folded: true,
      subFields: [
        {
          name: 'desktop',
          type: 'number',
          min: 1,
          max: 12,
          helperText: 'Max columns on Desktop',
        },
        {
          name: 'mobile',
          type: 'number',
          min: 1,
          max: 12,
          helperText: 'Max columns on Mobile',
        },
        {
          name: 'tablet',
          type: 'number',
          min: 1,
          max: 12,
          helperText: 'Max columns on Tablet',
        },
      ],
      defaultValue: {
        desktop: 3,
        mobile: 1,
        tablet: 2,
      },
    } as any,
    {
      name: 'columns',
      type: 'array',
      broadcast: true,
      subFields: [
        {
          name: 'blocks',
          type: 'array',
          hideFromUI: true,
          defaultValue: defaultBlocks,
        },
        {
          name: 'width',
          type: 'number',
          hideFromUI: true,
          helperText: 'Width %, e.g. set to 50 to fill half of the space',
        },
        {
          name: 'link',
          type: 'url',
          helperText: 'Optionally set a url that clicking this column will link to',
        },
      ],
      defaultValue: [
        { blocks: defaultBlocks },
        { blocks: defaultBlocks },
        { blocks: defaultBlocks },
      ],
      onChange: (options: Map<string, any>) => {
        function clearWidths() {
          columns.forEach((col) => {
            col.delete('width')
          })
        }

        const columns = options.get('columns') as Array<Map<string, any>>

        if (Array.isArray(columns)) {
          const containsColumnWithWidth = !!columns.find((col) => col.get('width'))

          if (containsColumnWithWidth) {
            const containsColumnWithoutWidth = !!columns.find((col) => !col.get('width'))
            if (containsColumnWithoutWidth) {
              clearWidths()
            } else {
              const sumWidths = columns.reduce((memo, col) => {
                return memo + (col.get('width') as number)
              }, 0)
              const widthsDontAddUp = sumWidths !== 100
              if (widthsDontAddUp) {
                clearWidths()
              }
            }
          }
        }
      },
    },
    {
      name: 'alignItems',
      type: 'string',
      defaultValue: 'left',
      enum: ['center', 'left', 'right'],
    },
    {
      name: 'responsiveSpace',
      friendlyName: 'Responsive gap between columns',
      type: 'object',
      subFields: [
        {
          name: 'desktop',
          type: 'number',
          min: 0,
          helperText: 'Spacing on Desktop',
        },
        {
          name: 'mobile',
          type: 'number',
          min: 0,
          helperText: 'Spacing on Mobile',
        },
        {
          name: 'tablet',
          type: 'number',
          min: 0,
          helperText: 'Spacing on Tablet',
        },
      ],
      defaultValue: {
        desktop: 40,
        mobile: 20,
        tablet: 20,
      },
    } as any,
  ],
  image: 'https://unpkg.com/@tabler/icons@2.44.0/icons/grid-dots.svg',
})
